/* Copyright 2019 Ellucian Company L.P. and its affiliates.
 * File: SignUp.tsx
 * Type: Container component */

// #region Imports
import React from 'react';

// Core components
import Button from '@powercampus/design-system/react/core/Button';
import Grid from '@powercampus/design-system/react/core/Grid';
import Modal from '@powercampus/design-system/react/core/Modal';
import Paragraph from '@powercampus/design-system/react/core/Paragraph';
import Text from '@powercampus/design-system/react/core/Text';
import TextField from '@powercampus/design-system/react/core/TextField';

// Types
import { IAlert } from '@powercampus/design-system/types/IAlert';
import { IJsonResult } from '@powercampus/design-system/types/IJsonResult';
import { ILogData } from '@powercampus/design-system/types/ILogData';
import { ResultType } from '@powercampus/design-system/types/ResultType';
import { IIdentityAccount, IIdentityAccountValidations, IResultSignUp } from '../../Types/Account/IIdentityAccount';
import { ChangePasswordResponse } from '../../Types/Enum/ChangePasswordResponse';
import { ISignUpResources } from '../../Types/Resources/Generic/ISignUpResources';

// Helpers
import { emailIsValid } from '@powercampus/design-system/helpers/CharRegExp';
import Format from '@powercampus/design-system/helpers/Format';
import LogData from '@powercampus/design-system/helpers/LogData';
import Tokens from '@powercampus/design-system/react/core/styles/Tokens';
import { createStyles, withStyles, WithStyles } from '@powercampus/design-system/react/core/styles/withStyles';

// Requests
import RequestsLayout from '@powercampus/design-system/requests/Layout';
import Requests from '../../Requests/Generic/SignUp';

// State Management
import LayoutActions from '@powercampus/design-system/flux/actions/LayoutActions';
// #endregion Imports

// #region Types
export interface ISignUpProps {
    open?: boolean;
    type: 'coned' | 'application' | 'shared';
    onAfterSignUp?: (userName?: string) => void;
    onClose?: () => void;
    onGoSignIn?: () => void;
}

interface ISignUpState {
    account: IIdentityAccount;
    accountValidations: IIdentityAccountValidations;
    componentError: boolean;
    emailRegExp?: string;
    isLoadingSave: boolean;
    resources?: ISignUpResources;
}

const styles = createStyles({
    spacingButtons: {
        paddingTop: Tokens.spacingSmall
    },
    spacingInstructions: {
        paddingTop: Tokens.spacingSmall
    }
});

type PropsWithStyles = ISignUpProps & WithStyles<typeof styles>;
// #endregion Types

// #region Component
class SignUp extends React.Component<PropsWithStyles, ISignUpState> {
    private idModule: string;
    private idPage: string;

    public readonly state: Readonly<ISignUpState>;

    public constructor(props) {
        super(props);

        // #region Initialize Variables and State
        this.idModule = 'Generic';
        this.idPage = 'SignUp';
        this.state = this.getInitialState();
        // #endregion Initialize Variables and State
    }

    private getInitialState(): ISignUpState {
        let resources: ISignUpResources | undefined;
        if (this.state) {
            resources = this.state.resources;
        }
        return {
            account: {
                email: '',
                firstName: '',
                lastName: '',
                password: ''
            },
            accountValidations: {
                confirmPassword: '',

                confirmPasswordModified: false,
                emailModified: false,
                firstNameModified: false,
                lastNameModified: false,
                passwordModified: false,

                emailInvalid: false,
                passwordInvalid: false,

                passwordInvalidMessage: '',

                existingUser: false
            },
            componentError: false,
            isLoadingSave: false,
            resources: resources
        };
    }

    // #region Events
    private onChangeTextField = (event: React.ChangeEvent<HTMLInputElement>): void => {
        try {
            const {
                account,
                accountValidations,
                emailRegExp
            } = this.state;

            const id: string = event.target.id;
            const value: string = event.target.value;
            switch (id) {
                case 'txtFirstName':
                    if (value.length <= 60) {
                        account.firstName = value;
                    }
                    accountValidations.firstNameModified = true;
                    break;
                case 'txtLastName':
                    if (value.length <= 60) {
                        account.lastName = value;
                    }
                    accountValidations.lastNameModified = true;
                    break;
                case 'txtEmail':
                    if (value.length <= 255) {
                        account.email = value;
                    }
                    if (emailRegExp && account.email) {
                        accountValidations.emailInvalid = !emailIsValid(account.email, emailRegExp);
                    }
                    else {
                        accountValidations.emailInvalid = false;
                    }
                    accountValidations.emailModified = true;
                    break;
                case 'txtPassword':
                    if (value.length <= 30) {
                        account.password = value;
                    }
                    accountValidations.passwordModified = true;
                    accountValidations.passwordInvalid = false;
                    accountValidations.passwordInvalidMessage = '';
                    break;
                case 'txtConfirmPassword':
                    if (value.length <= 30) {
                        accountValidations.confirmPassword = value;
                    }
                    accountValidations.confirmPasswordModified = true;
                    accountValidations.passwordInvalid = false;
                    accountValidations.passwordInvalidMessage = '';
                    break;
            }
            this.setState({
                account: account,
                accountValidations: accountValidations
            });
        }
        catch (e) {
            this.logError(LogData.fromException(this.onChangeTextField.name, e));
        }
    };

    private onCloseModal = (): void => {
        try {
            const {
                onClose
            } = this.props;

            const {
                isLoadingSave
            } = this.state;

            if (onClose && !isLoadingSave) {
                this.setState(this.getInitialState());
                onClose();
            }
        }
        catch (e) {
            this.logError(LogData.fromException(this.onCloseModal.name, e));
        }
    };

    private onCreateAccount = (): void => {
        try {
            const {
                type
            } = this.props;
            const {
                account,
                accountValidations,
                isLoadingSave
            } = this.state;

            if (!isLoadingSave) {
                accountValidations.confirmPasswordModified = true;
                accountValidations.emailModified = true;
                accountValidations.firstNameModified = true;
                accountValidations.lastNameModified = true;
                accountValidations.passwordModified = true;
                this.setState({
                    account: account,
                    accountValidations: accountValidations
                });

                if (Boolean(accountValidations.confirmPassword)
                    && Boolean(account.email)
                    && !accountValidations.emailInvalid
                    && Boolean(account.firstName)
                    && Boolean(account.lastName)
                    && Boolean(account.password)
                    && account.password === accountValidations.confirmPassword) {
                    this.showLoaderSave();
                    switch (type) {
                        case 'application':
                            Requests.postSignUpApplication(account, this.resolvePostSignUp, this.logError);
                            break;
                        case 'coned':
                            Requests.postSignUpConed(account, this.resolvePostSignUp, this.logError);
                            break;
                    }
                }
            }
        }
        catch (e) {
            this.logError(LogData.fromException(this.onCreateAccount.name, e));
        }
    };

    private onGoSignInModal = (): void => {
        try {
            const {
                onGoSignIn
            } = this.props;

            const {
                isLoadingSave
            } = this.state;

            if (onGoSignIn && !isLoadingSave) {
                this.setState(this.getInitialState());
                onGoSignIn();
            }
        }
        catch (e) {
            this.logError(LogData.fromException(this.onGoSignInModal.name, e));
        }
    };
    // #endregion Events

    // #region Loader Functions
    private hideAllLoaders = (): void => {
        this.setState({
            isLoadingSave: false
        });
    };

    private hideLoaderSave = (): void => {
        this.setState({
            isLoadingSave: false
        });
    };

    private showLoaderSave = (): void => {
        this.setState({
            isLoadingSave: true
        });
    };
    // #endregion Loader Functions

    // #region Error Functions
    private logError(logData: ILogData): void {
        this.hideAllLoaders();
        LayoutActions.setLogData(logData);
    }

    private redirectError(code: number): void {
        this.hideAllLoaders();
        LayoutActions.setRedirectCode(code);
    }

    private showError(message?: string): void {
        this.hideAllLoaders();
        LayoutActions.setAlert({ message: message, messageType: ResultType.error } as IAlert);
    }
    // #endregion Error Functions

    // #region Resolvers
    private resolveGetEmailRegExp = (json: string): void => {
        try {
            const result: IJsonResult = JSON.parse(json);
            if (result.status) {
                this.setState({
                    emailRegExp: result.data
                });
            }
            else if (result.code) {
                this.redirectError(result.code);
            }
            else if (result.log) {
                this.showError();
            }
            else {
                this.logError(LogData.badJsonResult(this.resolveGetEmailRegExp.name));
            }
        }
        catch (e) {
            this.logError(LogData.fromException(this.resolveGetEmailRegExp.name, e));
        }
    };
    private resolveGetResources = (json: string): void => {
        try {
            const result: IJsonResult = JSON.parse(json);
            if (result.status) {
                this.setState({
                    resources: result.data
                });
            }
            else if (result.code) {
                this.redirectError(result.code);
            }
            else if (result.log) {
                this.showError();
            }
            else {
                this.logError(LogData.badJsonResult(this.resolveGetResources.name));
            }
        }
        catch (e) {
            this.logError(LogData.fromException(this.resolveGetResources.name, e));
        }
    };

    private resolvePostSignUp = (json: string): void => {
        try {
            const result: IJsonResult = JSON.parse(json);
            if (result.status) {
                const resultSignUp: IResultSignUp = result.data;
                if (resultSignUp.status === 0) {
                    const {
                        onAfterSignUp
                    } = this.props;

                    const {
                        resources
                    } = this.state;

                    if (resources) {
                        LayoutActions.setAlert({
                            message: resources.lblCreatedSuccessfully,
                            messageType: ResultType.success
                        } as IAlert);
                        if (onAfterSignUp) {
                            onAfterSignUp(result.data.userName);
                        }
                        this.setState(this.getInitialState());
                    }
                }
                else if (resultSignUp.status === 1) {
                    const {
                        accountValidations,
                        resources
                    } = this.state;

                    if (accountValidations && resources) {
                        accountValidations.passwordInvalid = true;
                        switch (resultSignUp.passwordValidation) {
                            case ChangePasswordResponse.digits:
                                accountValidations.passwordInvalidMessage = resources.lblPasswordDigits;
                                break;
                            case ChangePasswordResponse.lowerCase:
                                accountValidations.passwordInvalidMessage = resources.lblPasswordLowerCase;
                                break;
                            case ChangePasswordResponse.minLength:
                                accountValidations.passwordInvalidMessage
                                    = Format.toString(resources.formatPasswordMinLength, [resultSignUp.passwordMinLength]);
                                break;
                            case ChangePasswordResponse.symbols:
                                accountValidations.passwordInvalidMessage = resources.lblPasswordSymbols;
                                break;
                            case ChangePasswordResponse.upperCase:
                                accountValidations.passwordInvalidMessage = resources.lblPasswordUpperCase;
                                break;
                        }

                        this.setState({
                            accountValidations: accountValidations
                        });
                    }
                }
                else if (resultSignUp.status === -1) {
                    const {
                        accountValidations
                    } = this.state;

                    if (accountValidations) {
                        accountValidations.existingUser = true;
                        this.setState({
                            accountValidations: accountValidations
                        });
                    }
                }
                else {
                    this.showError();
                }
                this.hideLoaderSave();
            }
            else if (result.code) {
                this.redirectError(result.code);
            }
            else if (result.log) {
                this.showError();
            }
            else {
                this.logError(LogData.badJsonResult(this.resolvePostSignUp.name));
            }
        }
        catch (e) {
            this.logError(LogData.fromException(this.resolvePostSignUp.name, e));
        }
    };
    // #endregion Resolvers

    // #region Lifecycle
    public componentDidMount(): void {
        try {
            RequestsLayout.getResources(this.idModule, this.idPage,
                this.resolveGetResources,
                this.logError);
            Requests.getEmailRegExp(this.resolveGetEmailRegExp, this.logError);
        }
        catch (e) {
            this.logError(LogData.fromException(this.componentDidMount.name, e));
        }
    }

    public componentDidCatch(error, info): void {
        this.setState({
            componentError: true
        }, () => {
            this.logError(LogData.fromComponentException(this.componentDidCatch.name, error, info));
            this.redirectError(500);
        });
    }
    // #endregion Lifecycle

    public render(): JSX.Element {
        const {
            classes,
            open,
            onClose,
            onGoSignIn
        } = this.props;

        const {
            account,
            accountValidations,
            componentError,
            isLoadingSave,
            resources
        } = this.state;

        let contentPage: JSX.Element | undefined;
        if (!componentError && resources) {
            let title: JSX.Element | undefined;
            let newAccountContent: JSX.Element | JSX.Element[] | undefined;
            let button: JSX.Element | undefined;

            // #region First Name
            let errorFirstName: boolean = false;
            let errorTextFirstName: string | undefined;
            if (accountValidations.firstNameModified) {
                errorFirstName = !Boolean(account.firstName);
                errorTextFirstName = errorFirstName ?
                    resources.lblFirstNameRequired
                    : undefined;
            }
            // #endregion First Name

            // #region Last Name
            let errorLastName: boolean = false;
            let errorTextLastName: string | undefined;
            if (accountValidations.lastNameModified) {
                errorLastName = !Boolean(account.lastName);
                errorTextLastName = errorLastName ?
                    resources.lblLastNameRequired
                    : undefined;
            }
            // #endregion Last Name

            // #region  Email
            let errorEmail: boolean = false;
            let errorTextEmail: string | undefined;
            if (accountValidations.emailModified) {
                errorEmail = !Boolean(account.email)
                    || accountValidations.emailInvalid
                    || accountValidations.existingUser;
                errorTextEmail = !Boolean(account.email) ?
                    resources.lblEmailRequired
                    : (accountValidations.emailInvalid ?
                        resources.lblEmailInvalid
                        : accountValidations.existingUser ?
                            resources.lblExistingUser
                            : undefined);
            }
            // #endregion Email

            // #region Password
            let errorPassword: boolean = false;
            let errorTextPassword: string | undefined;
            if (accountValidations.passwordModified) {
                errorPassword = !Boolean(account.password)
                    || accountValidations.passwordInvalid;
                errorTextPassword = !Boolean(account.password) ?
                    resources.lblPasswordRequired
                    : (accountValidations.passwordInvalid ?
                        accountValidations.passwordInvalidMessage
                        : undefined);
            }
            // #endregion Password

            // #region Confirm password
            let errorConfirmPassword: boolean = false;
            let errorTextConfirmPassword: string | undefined;
            if (accountValidations.confirmPasswordModified) {
                errorConfirmPassword = !Boolean(accountValidations.confirmPassword)
                    || accountValidations.confirmPassword !== account.password;
                errorTextConfirmPassword = !Boolean(accountValidations.confirmPassword) ?
                    resources.lblConfirmPasswordRequired
                    : (accountValidations.confirmPassword !== account.password ?
                        resources.lblPasswordsNotMatch : undefined);
            }
            // #endregion Confirm password

            title = (
                <>
                    <Text size="h2" align="center">
                        {resources.lblCreateAccount}
                    </Text>
                    {onGoSignIn && (
                        <Paragraph
                            align="center"
                            className={classes.spacingInstructions}
                            id="prgSignIn"
                            text={resources.lblInstructions}
                            events={[this.onGoSignInModal]}
                        />
                    )}
                </>
            );
            button = (
                <Button
                    id="btnCreateAccount"
                    loading={isLoadingSave}
                    onClick={this.onCreateAccount}
                >
                    {resources.btnCreateAccount}
                </Button>
            );
            newAccountContent = (
                <>
                    {!onClose && (
                        <Grid container>
                            <Grid item>
                                {title}
                            </Grid>
                        </Grid>
                    )}
                    <Grid container>
                        <Grid item xs={12}>
                            <Text size="h3">
                                {resources.lblContactInformation}
                            </Text>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                disabled={isLoadingSave}
                                error={errorFirstName}
                                id="txtFirstName"
                                helperText={errorTextFirstName}
                                label={resources.lblFirstName}
                                maxCharacters={60}
                                required
                                value={account.firstName}
                                onChange={this.onChangeTextField}
                                onEnterPress={this.onCreateAccount}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                disabled={isLoadingSave}
                                error={errorLastName}
                                id="txtLastName"
                                helperText={errorTextLastName}
                                label={resources.lblLastName}
                                maxCharacters={60}
                                required
                                value={account.lastName}
                                onChange={this.onChangeTextField}
                                onEnterPress={this.onCreateAccount}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                disabled={isLoadingSave}
                                error={errorEmail}
                                id="txtEmail"
                                helperText={errorTextEmail}
                                label={resources.lblEmail}
                                maxCharacters={255}
                                required
                                value={account.email}
                                onChange={this.onChangeTextField}
                                onEnterPress={this.onCreateAccount}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                disabled={isLoadingSave}
                                error={errorPassword}
                                id="txtPassword"
                                helperText={errorTextPassword}
                                label={resources.lblPassword}
                                maxCharacters={30}
                                passwordToggle
                                required
                                type="password"
                                value={account.password}
                                onChange={this.onChangeTextField}
                                onEnterPress={this.onCreateAccount}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                disabled={isLoadingSave}
                                error={errorConfirmPassword}
                                id="txtConfirmPassword"
                                helperText={errorTextConfirmPassword}
                                label={resources.lblConfirmPassword}
                                maxCharacters={30}
                                passwordToggle
                                required
                                type="password"
                                value={accountValidations.confirmPassword}
                                onChange={this.onChangeTextField}
                                onEnterPress={this.onCreateAccount}
                            />
                        </Grid>
                    </Grid>
                    {!onClose && (
                        <Grid
                            container
                            alignItems="flex-end"
                            className={classes.spacingButtons}
                            direction="column"
                        >
                            <Grid item>
                                {button}
                            </Grid>
                        </Grid>
                    )}
                </>
            );

            if (onClose) {
                contentPage = (
                    <Modal
                        disableBackdropClick={isLoadingSave}
                        disableEscapeKeyDown={isLoadingSave}
                        disableHeaderTypography
                        id="createAccountModal"
                        footer={button}
                        header={title}
                        maxWidth="md"
                        open={open}
                        onClose={this.onCloseModal}
                    >
                        {newAccountContent}
                    </Modal>
                );
            }
            else {
                contentPage = (
                    <>
                        {newAccountContent}
                    </>
                );
            }
        }

        return (
            <>
                {contentPage}
            </>
        );
    }
}
// #endregion Component

// Export: Component
export default withStyles(styles)(SignUp);